import type {
  DeckQaUserPromptInput,
  VisionShapeManifestInput,
  VisionReviewContextPromptInput,
} from "./types";

export const VISION_REVIEW_PROMPT = `You are a visual QA reviewer for PowerPoint slides. You ONLY fix layout/positioning problems. You do NOT change content, text, colors, fonts, or design.

You receive an image of the slide and the actual shape data (IDs, positions, sizes).

CHECK FOR THESE ISSUES ONLY:
1. **Text/shapes overlapping each other** — nudge one element's top or left to separate them
2. **Elements cut off or outside slide bounds** — move back into visible area
3. **Text overflowing its container** — slightly increase width or height

DO NOT:
- Change any text content, font sizes, font families, colors, or bold/italic
- Redesign or restructure the layout
- Move elements that aren't overlapping or cut off
- Add or delete shapes (action must always be "modify")
- Make aesthetic changes — only fix functional layout problems

RULES:
- Use EXACT shape IDs from the provided shape data
- Only adjust "left", "top", "width", or "height" properties
- Make MINIMAL changes — move shapes the smallest amount needed to fix the overlap
- If the slide looks fine, say so

If NO issues:
{
  "needsRefinement": false,
  "feedback": "No overlaps or cut-off elements detected."
}

If there ARE overlaps or cut-off elements:
{
  "needsRefinement": true,
  "feedback": "Shape X overlaps Shape Y — moved Y down by 20pt",
  "suggestedChanges": {
    "slides": [
      {
        "index": 0,
        "shapes": [
          { "action": "modify", "id": "ACTUAL_SHAPE_ID", "top": 370 }
        ]
      }
    ]
  }
}

Respond with JSON only.`;

export const SLIDE_QA_PROMPT = `You are a presentation reviewer. Analyze this slide for issues.

Check for: typos, grammar, factual concerns, design issues, clarity problems.

RESPOND WITH COMPACT JSON ONLY - keep descriptions SHORT (under 100 chars each):
{
  "overallScore": 8,
  "summary": "One sentence assessment",
  "issues": [
    {"type": "typo", "severity": "high", "description": "Short issue description", "suggestion": "Fix"}
  ],
  "keyFacts": [
    {"label": "Revenue", "value": "$1.5M"}
  ]
}

RULES:
- Maximum 5 issues per slide
- Keep each description under 100 characters
- keyFacts: only include 3-5 most important numbers/percentages
- No markdown, no explanation, ONLY the JSON object`;

export const SLIDE_QA_WITH_CONTEXT_PROMPT = `You are a presentation reviewer. Analyze this slide and check consistency with previous slides.

Check for: typos, grammar, factual concerns, design issues, clarity, AND contradictions with provided key facts.

RESPOND WITH COMPACT JSON ONLY - keep descriptions SHORT (under 100 chars each):
{
  "overallScore": 8,
  "summary": "One sentence assessment",
  "issues": [
    {"type": "consistency", "severity": "high", "description": "Short issue description", "suggestion": "Fix"}
  ],
  "keyFacts": [
    {"label": "Revenue", "value": "$2M"}
  ]
}

RULES:
- Maximum 5 issues per slide
- Keep each description under 100 characters
- keyFacts: only include 3-5 most important numbers/percentages
- Flag any values that contradict the provided key facts from previous slides
- No markdown, no explanation, ONLY the JSON object`;

export function buildDeckQaUserPrompt(input: DeckQaUserPromptInput): string {
  let userText = `This is slide ${input.slideIndex + 1} of ${input.totalSlides}. Please review it for typos, errors, and suggestions.`;

  if (input.priorFacts && input.priorFacts.length > 0) {
    const factsContext = input.priorFacts
      .map((fact) => `- Slide ${fact.slideIndex + 1}: ${fact.label} = ${fact.value}`)
      .join("\n");
    userText += `\n\n**KEY FACTS FROM PREVIOUS SLIDES (check for consistency):**\n${factsContext}`;
  }

  userText += `\n\nRespond with JSON only.`;
  return userText;
}

export function buildVisionReviewContextPrompt(input: VisionReviewContextPromptInput): string {
  return `This is slide ${input.slideIndex + 1}. The user requested: "${input.originalQuery}".

Slide dimensions: ${input.slideWidth} x ${input.slideHeight} points.

${input.shapeManifest}

Please review the slide image and check if there are any visual issues (overlaps, elements cut off, poor spacing). Use the exact shape IDs above when suggesting modifications. Respond with JSON.`;
}

export function buildVisionShapeManifest(input: VisionShapeManifestInput): string {
  if (!input.shapes.length) {
    return "No shape data available - slide may be empty or shapes could not be read.";
  }
  return `**ACTUAL SHAPES ON THIS SLIDE (use these exact IDs for modifications):**\n${input.shapes
    .map((shape) => {
      const lines = [
        `- ID: "${shape.id}"`,
        `  Name: ${shape.name || "(unnamed)"}`,
        `  Type: ${shape.type}`,
      ];
      if (shape.text) {
        lines.push(`  Text: "${shape.text.slice(0, 50)}${shape.text.length > 50 ? "..." : ""}"`);
      }
      lines.push(`  Position: left=${shape.left}, top=${shape.top}`);
      lines.push(`  Size: width=${shape.width}, height=${shape.height}`);
      return lines.join("\n");
    })
    .join("\n")}`;
}
